import to from './to'
import { fetchUtils } from 'react-admin'
import * as authManager from './authManager'

export type Methods = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

const getHeaders = (isFormData = false) => {
  const Authorization = authManager.getAccessToken() ?? ''
  const header: { Authorization: string, 'Content-Type'?: string, Accept: string } = {
    Authorization,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }
  if (isFormData) {
    delete header['Content-Type']
  }

  return new Headers(header)
}

/**
 * Renova e repete uma vez quando a API devolve 401.
 *
 * - 403 é falta de permissão e 5xx é servidor: nenhum dos dois renova nem desloga.
 * - retry ÚNICO; um segundo 401 sobe e o authProvider manda ao login.
 * - `attempt` remonta os headers a cada tentativa — reaproveitar os da primeira
 *   reenviaria o token morto.
 * - o corpo é remontado dos argumentos originais, nunca de um `Request` já consumido,
 *   porque os uploads mandam `FormData`.
 */
const withAuthRetry = async <T>(attempt: () => Promise<T>): Promise<T> => {
  const token = await authManager.ensure()

  try {
    return await attempt()
  } catch (err) {
    if ((err as { status?: number })?.status !== 401) { throw err }

    authManager.invalidate(token)
    if (!await authManager.renew()) { throw err }

    return attempt()
  }
}

export const httpClientReactAdmin = (
  url: string,
  options: RequestInit = {},
  isFormData = false,
) => {
  const callerHeaders = options.headers
  return withAuthRetry(() => fetchUtils.fetchJson(url, {
    ...options,
    headers: callerHeaders ?? getHeaders(isFormData),
  }))
}

const resolveFetch = async (res: Response) => {
  const json = await res.json()
  if (!res.ok) {
    // O interceptor decide pelo status, e o corpo do Boom não o traz.
    if (json && typeof json === 'object' && json.status === undefined) {
      json.status = res.status
    }
    throw json
  }
  return json
}

const getConfig = (method, payload): RequestInit => {
  const isFormData = payload instanceof FormData
  const headers = getHeaders(isFormData)
  const body = isFormData ? payload : JSON.stringify(payload || '{}')
  const withBody = payload ? { body } : {}

  return {
    method,
    ...withBody,
    headers,
  }
}

export const request = async <T, P = unknown>(
  url: string,
  method: Methods,
  payload?: P,
) => {
  const [err, results] = await to(
    withAuthRetry(() => fetch(url, getConfig(method, payload)).then(resolveFetch)),
  )

  if (err) {
    throw err
  }

  return results as T
}

export const get = <T>(url: string) => request<T>(url, 'GET')

export const post = <T>(url: string, payload: unknown) =>
  request<T>(url, 'POST', payload)

export const destroy = <T>(url: string) => request<T>(url, 'DELETE')

/** GET que devolve o corpo como texto (ex.: HTML). Mesma auth e renovação de token do `request`. */
export const getText = (url: string) =>
  withAuthRetry(async () => {
    const res = await fetch(url, { headers: getHeaders() })
    if (!res.ok) { throw { status: res.status } }
    return res.text()
  })
